import { Icon, Panel as GuardianPanel, Tooltip } from '@ics-portal/guardian';
import { type ReactNode } from 'react';
import { ModalDivided } from '@components/_atoms/ModalDivided/ModalDivided';
import { type ZoneModels } from '@models/Zone';
import { isDefined } from '@utils/isDefined';
import { DetailsSection, ProfilesSection } from './components';

interface PanelHeaderProps {
    name: string;
    standardId: string;
    hasSelectedProfiles: boolean;
    hasGuidance?: boolean;
}

function PanelHeader({
    name,
    standardId,
    hasSelectedProfiles,
    hasGuidance,
}: PanelHeaderProps): ReactNode {
    const guidanceIconId = `guidance-icon-${standardId}`;

    return (
        <span className="flex align-items-center gap-1">
            {hasSelectedProfiles ? (
                <span className="flex text-se40">
                    <Icon name="check_circle" fontSize="medium" />
                </span>
            ) : null}
            {name}
            {hasGuidance ? (
                <>
                    <span id={guidanceIconId} className="flex text-p70">
                        <Icon name="flowchart" />
                    </span>
                    <Tooltip
                        target={`#${guidanceIconId}`}
                        content="This Standard has Project Guidance"
                    />
                </>
            ) : null}
        </span>
    );
}

interface PanelProps {
    standard: ZoneModels.Standard;
    selectedProfileIds: string[];
    onProfileChange: (profileId: string, checked: boolean) => void;
    onStandardClear: (standardId: string) => void;
    allowedProfileIds?: string[];
    zoneId?: string;
    projectId?: string;
    readonly?: boolean;
    hasGuidance?: boolean;
    invalid?: boolean;
    singleSelect?: boolean;
    collapsed?: boolean;
    onToggle?: () => void;
}

export function Panel({
    standard,
    selectedProfileIds,
    onProfileChange,
    onStandardClear,
    allowedProfileIds,
    zoneId,
    projectId,
    readonly,
    hasGuidance,
    invalid,
    singleSelect,
    collapsed = true,
    onToggle,
}: PanelProps): ReactNode {
    const hasSelectedProfiles = (standard.standardProfiles ?? [])
        .filter(isDefined)
        .some((profile) => selectedProfileIds.includes(profile.id));

    return (
        <GuardianPanel
            header={
                <PanelHeader
                    name={standard.name}
                    standardId={standard.id}
                    hasSelectedProfiles={hasSelectedProfiles}
                    hasGuidance={hasGuidance}
                />
            }
            tertiary
            collapsed={collapsed}
            onToggle={onToggle}
            expandIcon="pi pi-chevron-right"
            collapseIcon="pi pi-chevron-down"
        >
            <div className="flex bg-n10 p-3">
                <ModalDivided>
                    <ProfilesSection
                        standard={standard}
                        selectedProfileIds={selectedProfileIds}
                        onProfileChange={onProfileChange}
                        allowedProfileIds={allowedProfileIds}
                        zoneId={zoneId}
                        projectId={projectId}
                        readonly={readonly}
                        invalid={invalid}
                        singleSelect={singleSelect}
                        onStandardClear={onStandardClear}
                    />
                    <DetailsSection standard={standard} />
                </ModalDivided>
            </div>
        </GuardianPanel>
    );
}
